import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUnreadMessageSummary } from '../api/auth';
import { getSmartiDocumentTitle } from '../branding/artavaBrand';
import styles from './AppMessageNotifier.module.css';

function readBaseTitle() {
  if (typeof document === 'undefined') return getSmartiDocumentTitle();
  return document.documentElement.dataset.smartiBaseTitle || getSmartiDocumentTitle();
}

export default function AppMessageNotifier({ isAuthenticated }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ totalUnread: 0, latestUnreadMessage: null });
  const [toast, setToast] = useState(null);
  const previousUnreadRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setSummary({ totalUnread: 0, latestUnreadMessage: null });
      previousUnreadRef.current = 0;
      if (typeof document !== 'undefined') {
        document.title = readBaseTitle();
      }
      return undefined;
    }

    let cancelled = false;

    const loadSummary = async () => {
      try {
        const nextSummary = await fetchUnreadMessageSummary();
        if (cancelled) return;
        setSummary(nextSummary || { totalUnread: 0, latestUnreadMessage: null });
        const nextUnread = Number(nextSummary?.totalUnread || 0);
        if (nextUnread > previousUnreadRef.current && nextSummary?.latestUnreadMessage) {
          setToast({
            senderDisplayName: nextSummary.latestUnreadMessage.senderDisplayName,
            bodyPreview: nextSummary.latestUnreadMessage.bodyPreview,
            senderUserId: nextSummary.latestUnreadMessage.senderUserId,
          });
        }
        previousUnreadRef.current = nextUnread;
      } catch (error) {
        console.warn('Unread message summary failed:', error);
      }
    };

    loadSummary();
    const interval = window.setInterval(loadSummary, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const baseTitle = readBaseTitle();
    const unread = Number(summary?.totalUnread || 0);
    document.title = unread > 0
      ? `(${unread}) ${baseTitle}`
      : baseTitle;
    return () => {
      document.title = readBaseTitle();
    };
  }, [summary?.totalUnread]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  if (!isAuthenticated || !toast) return null;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={() => {
        navigate(`/dashboard?openMessages=1&contact=${encodeURIComponent(String(toast.senderUserId || ''))}`);
        setToast(null);
      }}
    >
      <span className={styles.badge}>New message</span>
      <strong>{toast.senderDisplayName}</strong>
      <p>{toast.bodyPreview}</p>
      <em>Open inbox</em>
    </button>
  );
}
